import { FC, PropsWithChildren, useEffect, useState } from "react";

import { edgeLocalisationUrl } from "../constants/env";
import { ConsentModeContext } from "../contexts/ConsentModeContext";
import { hasAnalyticsConsent, hasMarketingConsent } from "../helpers/consent";
import {
  Cookiebot,
  COOKIEBOT_EVENTS,
  getConsentModeFromCookiebot,
  hasCookiebotDecision,
  submitConsentModeToCookiebot,
} from "../helpers/cookiebot";
import { pushGtmConsentUpdated, pushGtmLocalisation } from "../helpers/gtm";
import {
  CONSENT_MODE_KEY,
  getFromLocalStorage,
  getObjectFromLocalStorage,
  LEGACY_CONSENT_CARRIED_OVER_KEY,
  LOCALISATION_KEY,
  removeFromLocalStorage,
  setInLocalStorage,
  setObjectInLocalStorage,
} from "../helpers/localStorage";
import { ConsentMode } from "../types/consent";

type WindowWithZeffyHspManager = Window & {
  zeffyHspManager?: {
    applyHubspotConsent?: () => void;
  };
};

interface Localisation {
  countryCode: string;
  regionCode: string;
}

declare const window: WindowWithZeffyHspManager;

export const ConsentModeProvider: FC<PropsWithChildren> = ({ children }) => {
  const [consentMode, setConsentMode] = useState(getConsentModeFromLocalStorage());

  useEffect(() => {
    const storedLocalisation = getObjectFromLocalStorage<Localisation>?.(LOCALISATION_KEY);
    if (storedLocalisation) {
      pushGtmLocalisation({ country_code: storedLocalisation.countryCode, region_code: storedLocalisation.regionCode });
      return;
    }
    const referrerPolicy: ReferrerPolicy | undefined =
      process.env.NODE_ENV === "development" ? "unsafe-url" : undefined;
    void fetch(edgeLocalisationUrl, { method: "GET", referrerPolicy })
      .then(async (response) => {
        if (!response.ok) {
          return;
        }
        const { countryCode, regionCode }: Localisation = await response.json();
        if (countryCode) {
          setObjectInLocalStorage?.(LOCALISATION_KEY, { countryCode, regionCode });
        }
        pushGtmLocalisation({ country_code: countryCode, region_code: regionCode });
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const syncWithCookiebot = () => {
      const cookiebot = window.Cookiebot;
      if (!cookiebot || carryOverLegacyConsent(cookiebot)) {
        return;
      }
      const cookiebotConsentMode = getConsentModeFromCookiebot(cookiebot);
      saveConsentMode(cookiebotConsentMode);
      setConsentMode(cookiebotConsentMode);
    };

    if (window.Cookiebot && hasCookiebotDecision(window.Cookiebot)) {
      syncWithCookiebot();
    }
    COOKIEBOT_EVENTS.forEach((eventName) => window.addEventListener(eventName, syncWithCookiebot));
    return () => {
      COOKIEBOT_EVENTS.forEach((eventName) => window.removeEventListener(eventName, syncWithCookiebot));
    };
  }, []);

  return (
    <ConsentModeContext.Provider
      value={{
        hasConsentMode: Boolean(consentMode),
        // Functional consent (Amplitude) is granted to every visitor for now; use hasFunctionalityConsent(consentMode) to follow Cookiebot.
        hasFunctionalityConsentMode: true,
        hasAnalyticsConsentMode: hasAnalyticsConsent(consentMode),
        hasMarketingConsentMode: hasMarketingConsent(consentMode),
      }}
    >
      {children}
    </ConsentModeContext.Provider>
  );
};

const getConsentModeFromLocalStorage = (): ConsentMode | undefined =>
  getObjectFromLocalStorage<ConsentMode>?.(CONSENT_MODE_KEY);

const saveConsentMode = (consentMode: ConsentMode | undefined) => {
  if (JSON.stringify(consentMode) === JSON.stringify(getConsentModeFromLocalStorage())) {
    return;
  }
  if (consentMode) {
    setObjectInLocalStorage?.(CONSENT_MODE_KEY, consentMode);
  } else {
    removeFromLocalStorage?.(CONSENT_MODE_KEY);
  }
  pushGtmConsentUpdated();
  window.zeffyHspManager?.applyHubspotConsent?.();
};

const carryOverLegacyConsent = (cookiebot: Cookiebot): boolean => {
  if (getFromLocalStorage?.(LEGACY_CONSENT_CARRIED_OVER_KEY)) {
    return false;
  }
  setInLocalStorage?.(LEGACY_CONSENT_CARRIED_OVER_KEY, true);
  const legacyConsentMode = getConsentModeFromLocalStorage();
  if (!legacyConsentMode || hasCookiebotDecision(cookiebot)) {
    return false;
  }
  submitConsentModeToCookiebot(cookiebot, legacyConsentMode);
  return true;
};
